import { baseApi, apiTags } from '../baseApi';
import type {
    Client,
    ClientListParams,
    UpdateClientFieldParams,
    BindClientParams,
    UserFinanceGroupParams,
} from '../types/api.types';
import type { LaravelQueryBuilder } from '../types/laravelRequest';

export const clientsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getClients: builder.query<Client[], ClientListParams>({
            query: ({ clientId, isArchive, isFullGroup = false }) => ({
                resource: 'client',
                action: 'index',
                method: 'GET',
                useAll: true,
                queryBuilder: (query: LaravelQueryBuilder) => {
                    query.with('atonUsers')
                        .with('manager')
                        .with('multiple_managers_pivot.manager')
                        .with('mainContact', (contactQuery: LaravelQueryBuilder) =>
                            contactQuery
                                .with('refuseReason')
                                .with('tasks', (tasksQuery: LaravelQueryBuilder) =>
                                    tasksQuery.orderBy('end_at', 'DESC')
                                )
                        )
                        .with('multiple_managers');
                    
                    if (isFullGroup) {
                        query.with('financeGroups')
                            .with('financeGroups.unionUser')
                            .with('oppositeFinanceGroup')
                            .with('collectiveGroups')
                            .with('collectiveGroups.user')
                            .with('collectiveGroups.unionUser');
                    }

                    query.whereHas('mainContact', (contactQuery: LaravelQueryBuilder) =>
                        contactQuery.where('is_archive', isArchive ? 1 : 0)
                    );

                    if (clientId) {
                        query.where('id', clientId);
                    }

                    return query;
                },
            }),
            providesTags: (result) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Client, id: 'LIST' },
                          ...result.map((item) => ({ type: apiTags.Client, id: item.id })),
                      ]
                    : [{ type: apiTags.Client, id: 'LIST' }],
        }),
        updateClientField: builder.mutation<Client, UpdateClientFieldParams>({
            query: ({ clientId, params }) => ({
                resource: 'client',
                action: 'update-field',
                method: 'PUT',
                id: clientId,
                data: params,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Client, id: arg.clientId },
                { type: apiTags.Client, id: 'LIST' },
            ],
        }),
        bindClient: builder.mutation<unknown, BindClientParams>({
            query: ({ accountsToBind, queryParamsList }) => ({
                resource: 'client',
                action: 'bind',
                method: 'POST',
                data: accountsToBind.length > 0
                    ? { ...queryParamsList, services: accountsToBind }
                    : queryParamsList,
            }),
            invalidatesTags: [{ type: apiTags.Client, id: 'LIST' }],
        }),
        getClientTransitions: builder.query<unknown[], void>({
            query: () => ({
                resource: 'crm-client-transition',
                action: 'index',
                method: 'GET',
            }),
            providesTags: [{ type: apiTags.Client, id: 'TRANSITIONS' }],
        }),
        getUserFinanceGroup: builder.query<unknown[], UserFinanceGroupParams>({
            query: ({ userId }) => ({
                resource: 'user-finance-group',
                action: 'index',
                method: 'GET',
                queryBuilder: (query: LaravelQueryBuilder) => {
                    return query
                        .with('unionUser')
                        .where('user_id', userId);
                },
            }),
            providesTags: (result, error, arg) => [
                { type: apiTags.Client, id: `FINANCE_GROUP_${arg.userId}` },
            ],
        }),
    }),
});

export const {
    useGetClientsQuery,
    useUpdateClientFieldMutation,
    useBindClientMutation,
    useGetClientTransitionsQuery,
    useGetUserFinanceGroupQuery,
} = clientsApi;

